import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import i18next from "i18next";
import { makeStyles } from '@material-ui/core/styles';

import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';

import Loader from '../_shared/Loader';
import ScrollBody from '../_shared/ScrollBody';
import TeamAvatar from '../_shared/TeamAvatar';
import httpClient from '../_shared/HttpClient';
import { appStore } from '../../appStore';

const useStyles = makeStyles(theme => ({
    pageSection: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    playerHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    playerAvatar: {
        width: theme.spacing(9),
        height: theme.spacing(9),
        fontSize: '2rem',
    },
    rowLabel: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1),
    },
    sectionTitle: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    metricValue: {
        fontWeight: 700,
    },
    smallAvatar: {
        width: theme.spacing(4),
        height: theme.spacing(4),
    },
    muted: {
        fontStyle: 'italic',
    },
    teamChip: {
        marginRight: theme.spacing(0.75),
        marginTop: theme.spacing(0.5),
    },
}));

const PlayerPage = (props) => {
    const classes = useStyles();
    const { dispatch } = useContext(appStore);
    const { leagueID, playerID } = props.match.params;

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        dispatch({ type: 'updateNavigation', payload: { navTitle: 'Joueur' } });
    }, [dispatch]);

    useEffect(() => {
        props.setLeagueID(leagueID);
        loadData();
    }, [leagueID, playerID]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await httpClient('get', 'Calendar/GetPlayerPage', { leagueID, playerID });
            setData(result);
        } catch (err) {
            console.log(err);
        }
        setIsLoading(false);
    };

    const localizeText = (value) => {
        if (!value) return '';
        try {
            const parsed = JSON.parse(value);
            const language = i18next.languages && i18next.languages.length ? i18next.languages[0] : i18next.language;
            return parsed[language] || parsed.fr || parsed.en || value;
        } catch {
            return value;
        }
    };

    if (isLoading) {
        return <Loader isLoading={isLoading}><div></div></Loader>;
    }

    if (!data || !data.player) {
        return (
            <Paper className={classes.pageSection}>
                <Typography>Aucune donnee de joueur disponible.</Typography>
            </Paper>
        );
    }

    const fullName = `${data.player.name || ''}${data.player.defaultNumber ? ' #' + data.player.defaultNumber : ''}`.trim();
    const playerInitial = (data.player.name || '?').substring(0, 1).toUpperCase();

    return (
        <>
            <ListItem button dense disableGutters divider component={Link} to={`/home/calendar/${leagueID}`}>
                <ListItemAvatar>
                    <Avatar className={classes.smallAvatar}>
                        <BackIcon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Retour au calendrier" />
            </ListItem>

            <ScrollBody hasMorePrevious={false} hasMoreNext={false}>
                <Paper className={classes.pageSection}>
                    <div className={classes.playerHeader}>
                        <Avatar className={classes.playerAvatar}>{playerInitial}</Avatar>
                        <div>
                            <Typography variant="h4">{fullName || 'Joueur'}</Typography>
                            <Typography variant="body1">
                                <span className={classes.rowLabel}>Position:</span>
                                {localizeText(data.player.positionName) || data.player.positionCode || 'N/D'}
                            </Typography>
                            <Typography variant="body1">
                                <span className={classes.rowLabel}>Equipes actives:</span>
                                {(data.player.currentTeams || []).length > 0
                                    ? data.player.currentTeams.map(t => t.name).join(', ')
                                    : 'N/D'}
                            </Typography>
                        </div>
                    </div>
                </Paper>

                <Paper className={classes.pageSection}>
                    <Typography variant="h6" className={classes.sectionTitle}>Carriere</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={2}>
                            <Typography className={classes.rowLabel}>Saisons</Typography>
                            <Typography className={classes.metricValue}>{data.careerTotals ? data.careerTotals.seasonsCount : 0}</Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography className={classes.rowLabel}>Matchs</Typography>
                            <Typography className={classes.metricValue}>{data.careerTotals ? data.careerTotals.gamesPlayed : 0}</Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography className={classes.rowLabel}>Buts</Typography>
                            <Typography className={classes.metricValue}>{data.careerTotals ? data.careerTotals.goals : 0}</Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography className={classes.rowLabel}>Assistances</Typography>
                            <Typography className={classes.metricValue}>{data.careerTotals ? data.careerTotals.assists : 0}</Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography className={classes.rowLabel}>Points</Typography>
                            <Typography className={classes.metricValue}>{data.careerTotals ? data.careerTotals.points : 0}</Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper className={classes.pageSection}>
                    <Typography variant="h6" className={classes.sectionTitle}>Stats par saison</Typography>
                    {(data.seasons || []).length === 0 ? (
                        <Typography className={classes.muted}>Aucune statistique de saison disponible.</Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Saison</TableCell>
                                        <TableCell>Equipes</TableCell>
                                        <TableCell align="center">Matchs</TableCell>
                                        <TableCell align="center">Buts</TableCell>
                                        <TableCell align="center">Assistances</TableCell>
                                        <TableCell align="center">Points</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.seasons || []).map((season) => (
                                        <TableRow key={season.seasonID}>
                                            <TableCell>{season.seasonDescription || `Saison ${season.seasonID}`}</TableCell>
                                            <TableCell>
                                                {(season.teams || []).length === 0
                                                    ? 'N/D'
                                                    : (season.teams || []).map((team) => (
                                                        <Chip
                                                            key={`${season.seasonID}_${team.teamID || team.name}`}
                                                            size="small"
                                                            className={classes.teamChip}
                                                            avatar={<TeamAvatar team={team} className={classes.smallAvatar} />}
                                                            label={team.name}
                                                        />
                                                    ))}
                                            </TableCell>
                                            <TableCell align="center">{season.gamesPlayed}</TableCell>
                                            <TableCell align="center">{season.goals}</TableCell>
                                            <TableCell align="center">{season.assists}</TableCell>
                                            <TableCell align="center">{season.points}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </ScrollBody>
        </>
    );
};

export default PlayerPage;
